import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponseModel } from '../../@types/base';
import { CONSTS } from './const';
import { dispatch } from '../store';
import axios from '../../utils/axios';
import { HttpStatusCodes } from '../../constants/http-status-codes.enum';
import {
  AddCompanyModel,
  AddUserToCompanyModel,
  CompanyDetailModel,
  CompanyListModel,
  CompanyResultModel,
  EditCompanyModel
} from '../../@types/company';

// ----------------------------------------------------------------------

type CompanyState = {
  isAddUserToCompanyLoading: boolean;
  addUserToCompanyError: boolean;

  companyError: boolean;
  isCompanyLoading: boolean;
  companyList: CompanyListModel[];

  companyDetailError: boolean;
  isCompanyDetailLoading: boolean;
  companyDetail: CompanyDetailModel;

  createCompanyModalIsOpen: boolean;
  updateCompanyModalIsOpen: boolean;

  addUserToCompanyModalIsOpen: boolean;
};

const companyDetailInit: CompanyDetailModel = {
  id: 0,
  name: '',
  numberOfCams: -1,
  createdAt: '',
  updatedAt: ''
  // companyId: 0
};

const initialState: CompanyState = {
  isAddUserToCompanyLoading: false,
  addUserToCompanyError: false,
  companyError: false,
  isCompanyLoading: false,
  companyList: [],

  companyDetailError: false,
  isCompanyDetailLoading: false,
  companyDetail: companyDetailInit,

  createCompanyModalIsOpen: false,
  updateCompanyModalIsOpen: false,
  addUserToCompanyModalIsOpen: false
};

const slice = createSlice({
  name: 'company',
  initialState,
  reducers: {

    startAddUserToCompanyLoading(state) {
      state.isAddUserToCompanyLoading = true;
    },

    // HAS ERROR ADD USER TO COMPANY
    hasErrorAddUserToCompany(state, action) {
      state.isAddUserToCompanyLoading = false;
      state.addUserToCompanyError = action.payload;
    },

    // START COMPANY LOADING
    startCompanyLoading(state) {
      state.isCompanyLoading = true;
    },

    // HAS ERROR COMPANY
    hasErrorCompany(state, action) {
      state.isCompanyLoading = false;
      state.companyError = action.payload;
    },

    // GET ALL COMPANIES
    getAllCompaniesSuccess(state, action) {
      state.isCompanyLoading = false;
      state.companyList = action.payload;
    },

    // ----------------------------------------------------------------------
    // COMPANY DETAIL
    // ----------------------------------------------------------------------

    // START COMPANY DETAIL LOADING
    startCompanyDetailLoading(state) {
      state.isCompanyDetailLoading = true;
    },

    // HAS ERROR COMPANY DETAIL
    hasErrorCompanyDetail(state, action) {
      state.isCompanyDetailLoading = false;
      state.companyDetailError = action.payload;
    },

    // GET COMPANY DETAIL
    getCompanyDetailSuccess(state, action) {
      state.isCompanyDetailLoading = false;
      state.companyDetail = action.payload;
    },

    // COMPANY ADD MODAL OPEN
    onToggleCreateCompanyOpenModal(state, action) {
      state.createCompanyModalIsOpen = action.payload;
    },

    // COMPANY DETAIL MODAL OPEN
    onToggleUpdateCompanyOpenModal(state, action) {
      state.updateCompanyModalIsOpen = action.payload;
    },

    // ADD USER TO COMPANY MODAL OPEN
    onToggleAddUserToCompanyOpenModal(state, action) {
      state.addUserToCompanyModalIsOpen = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  onToggleCreateCompanyOpenModal,
  onToggleUpdateCompanyOpenModal,
  onToggleAddUserToCompanyOpenModal
} = slice.actions;

// ----------------------------------------------------------------------
// COMPANY

export async function setCompanyDetailNull() {
  try {
    dispatch(slice.actions.getCompanyDetailSuccess(companyDetailInit));
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCompanyDetail(error));
  }
}

export async function getCompanyByIdWithReturn(companyId: string) {
  dispatch(slice.actions.startCompanyDetailLoading());
  try {
    const response: AxiosResponseModel<CompanyResultModel> = await axios
      .get(`${CONSTS.COMPANY_GET_GET_COMPANY_BY_ID}/${companyId}`)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(slice.actions.getCompanyDetailSuccess(response.data?.data));
    } else {
      dispatch(slice.actions.getCompanyDetailSuccess(companyDetailInit));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCompanyDetail(error));
    return error.response.data;
  }
}

export async function addUserToCompanyWithReturn(user: AddUserToCompanyModel) {
  dispatch(slice.actions.startAddUserToCompanyLoading());
  try {
    const response: AxiosResponseModel<CompanyResultModel> = await axios
      .post(CONSTS.AUTH_POST_REGISTER, user)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(slice.actions.hasErrorAddUserToCompany(false));
    } else {
      dispatch(
        slice.actions.hasErrorAddUserToCompany(response.data?.message || '')
      );
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorAddUserToCompany(error));
    return error.response.data;
  }
}

export async function getAllCompaniesWithReturn() {
  dispatch(slice.actions.startCompanyLoading());
  try {
    const response: AxiosResponseModel<CompanyResultModel> = await axios
      .get(CONSTS.COMPANY_GET_GET_ALL_COMPANIES)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(slice.actions.getAllCompaniesSuccess(response.data?.data));
    } else {
      dispatch(slice.actions.getAllCompaniesSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCompany(error));
    return error.response.data;
  }
}

export async function addCompanyWithReturn(company: AddCompanyModel) {
  dispatch(slice.actions.startCompanyLoading());
  try {
    const response: AxiosResponseModel<CompanyResultModel> = await axios
      .post(CONSTS.COMPANY_POST_ADD_COMPANY, company)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      await getAllCompaniesWithReturn();
      dispatch(slice.actions.hasErrorCompany(false));
    } else {
      dispatch(slice.actions.hasErrorCompany(response.data?.message || ''));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCompany(error));
    return error.response.data;
  }
}

export async function editCompanyWithReturn(company: EditCompanyModel) {
  dispatch(slice.actions.startCompanyLoading());
  try {
    const response: AxiosResponseModel<CompanyResultModel> = await axios
      .post(`${CONSTS.COMPANY_POST_UPDATE_COMPANY}/${company.id}`, company)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      await getAllCompaniesWithReturn();
      dispatch(slice.actions.hasErrorCompany(false));
    } else {
      dispatch(slice.actions.hasErrorCompany(response.data?.message || ''));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCompany(error));
    return error.response.data;
  }
}

export async function deleteCompanyWithReturn(companyId: string) {
  // dispatch(slice.actions.startCompanyLoading());
  try {
    const response: AxiosResponseModel<CompanyResultModel> = await axios
      .post(`${CONSTS.COMPANY_POST_DELETE_COMPANY}/${companyId}`)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      await getAllCompaniesWithReturn();
      dispatch(slice.actions.hasErrorCompany(false));
    } else {
      dispatch(slice.actions.hasErrorCompany(response.data?.message || ''));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCompany(error));
    return error.response.data;
  }
}

// ----------------------------------------------------------------------
